// frontend/src/util/push_client_util.js

const urlBase64ToUint8Array = (base64String) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

const getBearerToken = () => {
  const stored = localStorage.getItem("jwtToken");
  if (!stored) return null;
  return stored.startsWith("Bearer ") ? stored : `Bearer ${stored}`;
};

const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent);
const isStandalonePwa = () =>
  window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator?.standalone === true;

const ensureServiceWorkerReady = async () => {
  let reg = await navigator.serviceWorker.getRegistration();
  if (!reg) {
    reg = await navigator.serviceWorker.register("/sw.js");
  }

  const ready = navigator.serviceWorker.ready;
  const timeoutMs = 8000;
  const timeout = new Promise((_, reject) =>
    setTimeout(() => reject(new Error("Service worker not ready yet. Please refresh and try again.")), timeoutMs)
  );

  return Promise.race([ready, timeout]);
};

export async function getPushStatus() {
  const supported = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
  if (!supported) return { supported: false, permission: "unsupported", subscribed: false };

  const permission = Notification.permission;

  if (permission !== "granted") {
    return { supported: true, permission, subscribed: false };
  }

  const reg = await ensureServiceWorkerReady();
  const sub = await reg.pushManager.getSubscription();
  return { supported: true, permission, subscribed: !!sub };
}

export async function enablePushNotifications() {
  if (!("serviceWorker" in navigator)) throw new Error("Service workers not supported.");
  if (!("PushManager" in window)) throw new Error("Push not supported on this browser/device.");
  if (!("Notification" in window)) throw new Error("Notifications not supported.");

  if (isIOS() && !isStandalonePwa()) {
    throw new Error("On iOS, install the app to your Home Screen before enabling notifications.");
  }

  const perm = await Notification.requestPermission();
  if (perm !== "granted") throw new Error("Notifications permission not granted.");

  const reg = await ensureServiceWorkerReady();

  const publicKey = (process.env.REACT_APP_VAPID_PUBLIC_KEY || "").trim();
  if (!publicKey) throw new Error("Missing REACT_APP_VAPID_PUBLIC_KEY in frontend .env");

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }

  const bearer = getBearerToken();
  if (!bearer) throw new Error("Not logged in (missing JWT).");

  const res = await fetch("/api/push/subscribe", {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: bearer },
    body: JSON.stringify({ subscription: sub }),
  });

  if (!res.ok) throw new Error((await res.text()) || "Failed to save push subscription.");

  return { success: true };
}

export async function disablePushNotifications() {
  const bearer = getBearerToken();
  if (!bearer) throw new Error("Not logged in.");

  const reg = await ensureServiceWorkerReady();
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return { success: true };

  // tell backend to remove it
  await fetch("/api/push/unsubscribe", {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: bearer },
    body: JSON.stringify({ endpoint: sub.endpoint }),
  });

  await sub.unsubscribe();
  return { success: true };
}
